import React from "react";
import { Plus, Trash2 } from "lucide-react";
import { useLang } from "../i18n";
import DateInput from "./DateInput";

export function Field({ label, children, className = "" }) {
  return (
    <div className={className}>
      <label className="ec-label">{label}</label>
      {children}
    </div>
  );
}

export function YesNo({ value, onChange, testid }) {
  const { t } = useLang();
  return (
    <div className="flex gap-2">
      {[true, false].map((v) => (
        <button
          key={String(v)}
          type="button"
          data-testid={`${testid}-${v ? "yes" : "no"}`}
          onClick={() => onChange(v)}
          className={`px-5 py-1.5 rounded-full text-sm font-semibold border transition-colors ${
            value === v
              ? "bg-[#16335F] text-white border-[#16335F]"
              : "bg-white text-slate-600 border-slate-300 hover:border-slate-500"
          }`}
        >
          {v ? t("common.yes") : t("common.no")}
        </button>
      ))}
    </div>
  );
}

export function CheckGroup({ options, values, onChange, testidPrefix }) {
  const toggle = (opt) => {
    onChange(values.includes(opt) ? values.filter((v) => v !== opt) : [...values, opt]);
  };
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((opt, i) => (
        <button
          key={opt}
          type="button"
          data-testid={`${testidPrefix}-${i}`}
          onClick={() => toggle(opt)}
          className={`px-3.5 py-1.5 rounded-full text-sm border transition-colors ${
            values.includes(opt)
              ? "bg-[#044CBC] text-white border-[#044CBC] font-semibold"
              : "bg-white text-slate-600 border-slate-300 hover:border-slate-500"
          }`}
        >
          {opt}
        </button>
      ))}
    </div>
  );
}

export function DynRows({ columns, rows, onChange, testid }) {
  const { t } = useLang();
  const setCell = (i, key, val) => {
    const next = rows.map((r, idx) => (idx === i ? { ...r, [key]: val } : r));
    onChange(next);
  };
  const addRow = () => onChange([...rows, Object.fromEntries(columns.map((c) => [c.key, c.type === "select" ? c.options[0] : ""]))]);
  const removeRow = (i) => onChange(rows.filter((_, idx) => idx !== i));
  return (
    <div className="space-y-2">
      <div className="overflow-x-auto rounded-lg border border-slate-200">
        <table className="w-full text-sm min-w-[560px]">
          <thead>
            <tr className="bg-slate-50 text-left">
              {columns.map((c) => (
                <th key={c.key} className="px-3 py-2 text-xs font-bold uppercase tracking-wide text-slate-500">{c.label}</th>
              ))}
              <th className="w-10" />
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={columns.length + 1} className="px-3 py-3 text-sm text-slate-400 italic">—</td></tr>
            )}
            {rows.map((row, i) => (
              <tr key={i} className="border-t border-slate-100" data-testid={`${testid}-row-${i}`}>
                {columns.map((c) => (
                  <td key={c.key} className="px-2 py-1.5">
                    {c.type === "select" ? (
                      <select className="ec-input" value={row[c.key] ?? ""} onChange={(e) => setCell(i, c.key, e.target.value)} data-testid={`${testid}-${c.key}-${i}`}>
                        {c.options.map((o) => <option key={o} value={o}>{o}</option>)}
                      </select>
                    ) : c.type === "date" ? (
                      <DateInput value={row[c.key] ?? ""} onChange={(e) => setCell(i, c.key, e.target.value)} data-testid={`${testid}-${c.key}-${i}`} />
                    ) : (
                      <input className="ec-input" type={c.type || "text"} value={row[c.key] ?? ""} onChange={(e) => setCell(i, c.key, e.target.value)} data-testid={`${testid}-${c.key}-${i}`} />
                    )}
                  </td>
                ))}
                <td className="px-1">
                  <button type="button" onClick={() => removeRow(i)} data-testid={`${testid}-remove-${i}`} className="text-slate-400 hover:text-red-600">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <button
        type="button"
        onClick={addRow}
        data-testid={`${testid}-add`}
        className="flex items-center gap-1.5 text-sm font-semibold text-[#044CBC] hover:text-[#033A8C]"
      >
        <Plus className="w-4 h-4" /> {t("common.add_row")}
      </button>
    </div>
  );
}
